import React from 'react';
import { useRecordContext, Button } from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Alert,
  Divider,
} from '@mui/material';
import {
  Assessment as QualityIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { leadImportFields } from '../../leadImports.model';
import auth from '../../../auth/auth0';
import { useApiConfig } from '../../../services/apiConfig';
import logger from '../../../utils/logger';
const QualityOverview = ({ record: recordProp, ...props }) => {
  const contextRecord = useRecordContext(props);
  const record = recordProp || contextRecord;
  const apiConfig = useApiConfig();

  if (!record) return null;

  const stats = record[leadImportFields.stats];
  if (!stats) return null;

  const {
    totalRows = 0,
    successfulRows = 0,
    qualityAssessment,
    hasDetailedReport = false,
    failedRows,
  } = stats;

  const failedCount = failedRows?.count || 0;

  // Calculate rates
  const successRate = totalRows > 0 ? (successfulRows / totalRows) * 100 : 0;
  const failureRate = totalRows > 0 ? (failedCount / totalRows) * 100 : 0;


  // Use quality assessment if available
  const quality = qualityAssessment?.quality;

  const assessmentFailureRate = qualityAssessment?.failureRate
    ? parseFloat(qualityAssessment.failureRate)
    : failureRate;
  const warnings = qualityAssessment?.warnings || [];

  const getQualityColor = (quality) => {
    switch (quality?.toLowerCase()) {
      case 'good':
        return 'success';
      case 'fair':
        return 'warning';
      case 'poor':
        return 'error';
      default:
        return 'default';
    }
  };

  const getQualityIcon = (quality) => {
    switch (quality?.toLowerCase()) {
      case 'good':
        return '✅';
      case 'fair':
        return '⚠️';
      case 'poor':
        return '❌';
      default:
        return '📊';
    }
  };

  const handleDownloadDetailedReport = async () => {
    try {
      const response = await fetch(
        `${apiConfig.getApiUrl('leadImports')}/lead-imports/${record.id}/detailed-report`,
        {
          headers: {
            Authorization: `Bearer ${auth.getIdToken()}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        window.open(data.downloadUrl, '_blank');
      } else {
        logger.error('Failed to get detailed report download URL');
      }
    } catch (error) {
      logger.error('Error downloading detailed report:', error);
    }
  };

  return (
    <Card
      sx={{
        mt: 2,
        background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
      }}
    >
      <CardContent>
        {/* Header with Quality Score */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QualityIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Import Quality Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalRows.toLocaleString()} total rows processed
              </Typography>
            </Box>
          </Box>

          {quality && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ mb: 1 }}>
                {getQualityIcon(quality)}
              </Typography>
              <Chip
                label={`${quality.toUpperCase()} QUALITY`}
                color={getQualityColor(quality)}
                size="large"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          )}
        </Box>

        {/* Progress Bars */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" fontWeight="medium">
                Success Rate
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="success.main"
              >
                {successRate.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={successRate}
              color="success"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {assessmentFailureRate > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="body2" fontWeight="medium">
                  Failure Rate
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="error.main"
                >
                  {assessmentFailureRate.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={assessmentFailureRate}
                color="error"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

        </Box>

        {/* Quality Warnings */}
        {warnings.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Quality Assessment Warnings:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {warnings.map((warning, index) => (
                  <li key={index}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          </>
        )}

        {/* Quick Stats */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              p: 1.5,
              bgcolor: 'success.50',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="success.main" fontWeight="bold">
              {successfulRows.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Successful
            </Typography>
          </Box>

          {failedCount > 0 && (
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: 'error.50',
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="error.main" fontWeight="bold">
                {failedCount.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Failed
              </Typography>
            </Box>
          )}


        </Box>

        {/* Actions */}
        {hasDetailedReport && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadDetailedReport}
              sx={{ fontWeight: 'bold' }}
            >
              Download Complete Report
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default QualityOverview;
